import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, img, money } from "../../lib/api";
import { STATUS_LABELS } from "../../lib/i18n";
import { Card, PageHeader } from "../../components/admin/ui";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-md border border-gray-200 bg-white p-5">
    <p className="text-xs text-gray-500">{label}</p>
    <p data-testid={testid} className="mt-2 text-2xl font-bold">{value}</p>
  </div>
);

export default function Dashboard() {
  const [days, setDays] = useState(30);
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get("/admin/reports", { params: { days } }).then((x) => setR(x.data));
  }, [days]);
  if (!r) return null;

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="تقارير المبيعات" desc="نظرة عامة على أداء المتجر">
        {[7, 30, 90].map((d) => (
          <button key={d} data-testid={`report-range-${d}`} onClick={() => setDays(d)} className={`rounded border px-3 py-1.5 text-sm ${days === d ? "border-ink bg-ink text-white" : "border-gray-300 bg-white"}`}>{d} يوم</button>
        ))}
      </PageHeader>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="إجمالي المبيعات (ر.س)" value={money(r.revenue)} testid="stat-revenue" />
        <Stat label="عدد الطلبات" value={r.orders} testid="stat-orders" />
        <Stat label="متوسط قيمة الطلب" value={money(r.avg_order)} testid="stat-avg" />
        <Stat label="المشتركات في القائمة البريدية" value={r.subscribers} testid="stat-subscribers" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card title="المبيعات اليومية" className="lg:col-span-2">
          <div className="h-64" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={r.series}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} /><stop offset="100%" stopColor="#D4AF37" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Area type="monotone" dataKey="revenue" stroke="#0A0A0A" fill="url(#g)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="حالات الطلبات">
          <div className="space-y-2">
            {Object.entries(r.status_counts).map(([s, n]) => (
              <Link key={s} to={`/admin/orders?status=${s}`} className="flex justify-between rounded px-2 py-1.5 text-sm hover:bg-gray-50">
                <span>{STATUS_LABELS[s].ar}</span><span className="font-bold">{n}</span>
              </Link>
            ))}
          </div>
        </Card>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="المنتجات الأكثر مبيعًا">
          {!r.top_products.length && <p className="text-sm text-gray-500">لا توجد مبيعات في هذه الفترة</p>}
          {r.top_products.map((p) => (
            <div key={p.product_id} className="flex items-center gap-3 border-b border-gray-100 py-2 text-sm last:border-0">
              <img src={img(p.image)} alt="" className="h-10 w-8 object-cover" />
              <span className="flex-1">{p.name_ar}</span><span>{p.qty} قطعة</span><span className="w-24 text-left">{money(p.revenue)} ر.س</span>
            </div>
          ))}
        </Card>
        <Card title="تنبيهات المخزون المنخفض">
          {!r.low_stock.length && <p className="text-sm text-gray-500">المخزون جيد</p>}
          <div className="max-h-72 overflow-y-auto">
            {r.low_stock.map((v, i) => (
              <Link key={i} to={`/admin/products/${v.product_id}`} className="flex justify-between border-b border-gray-100 py-2 text-sm last:border-0 hover:bg-gray-50">
                <span>{v.name_ar} <span className="text-gray-400">{[v.size, v.color].filter(Boolean).join(" / ")}</span></span>
                <span className={v.stock === 0 ? "font-bold text-red-600" : "text-amber-600"}>{v.stock}</span>
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
